const en = {
  app: {
    name: 'Loyalty Platform',
    ownerConsole: 'Owner console',
    companyCabinet: 'Company workspace',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    close: 'Close',
    confirm: 'Confirm',
    search: 'Search',
    loading: 'Loading…',
    refresh: 'Refresh',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    actions: 'Actions',
    status: 'Status',
    email: 'Email',
    password: 'Password',
    username: 'Username',
    role: 'Role',
    created: 'Created',
    lastLogin: 'Last sign-in',
    never: 'Never',
    you: 'You',
    noData: 'No data yet',
    error: 'Something went wrong',
    saved: 'Saved',
    pageOf: 'Page {page} of {pages}',
    total: 'Total: {count}',
    readOnly: 'View only',
  },
  roles: {
    superadmin: 'Platform owner',
    org_admin: 'Administrator',
    org_viewer: 'Viewer',
  },
  roleHints: {
    org_admin: 'Full access to the company workspace',
    org_viewer: 'Sees everything, changes nothing',
  },
  nav: {
    support: 'Support',
    groups: {
      platform: 'Platform',
      business: 'Business',
      system: 'System',
      company: 'Company',
      engagement: 'Engagement',
      settings: 'Settings',
    },
    overview: 'Overview',
    companies: 'Companies',
    billing: 'Billing',
    employees: 'Employees',
    analytics: 'Analytics',
    notifications: 'Campaigns',
    logs: 'Event log',
    security: 'Security',
    sql: 'SQL console',
    systemSettings: 'System settings',
    audit: 'Activity log',
    billingNav: 'Billing',
    home: 'Home',
    restaurants: 'Venues',
    branding: 'App',
    team: 'Team',
    company: 'Company',
    myCompany: 'My company',
  },
  topbar: {
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    logout: 'Sign out',
    openCompany: 'Open company workspace',
    chooseCompany: 'Choose a company',
    menu: 'Menu',
  },
  viewAs: {
    banner: 'You are viewing the “{name}” workspace as the platform owner',
    exit: 'Back to console',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Employee loyalty program management',
    usernameLabel: 'Username or email',
    passwordLabel: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    invalid: 'Invalid username or password',
    codeLabel: 'Verification code',
    codeHint: 'Open your authenticator app and enter the 6-digit code for this panel.',
    codeInvalid: 'Invalid code. Make sure your phone sets the time automatically.',
    back: 'Use another account',
    sideTitle: 'One platform — many companies',
    sideText: 'Employees, cards, analytics and campaigns of every company — separated and under control.',
  },
  team: {
    title: 'Team',
    subtitle: 'Who has access to the company workspace',
    invite: 'Add member',
    inviteTitle: 'New team member',
    editTitle: 'Team member',
    newPassword: 'New password',
    newPasswordHint: 'Leave empty to keep the current one',
    passwordHint: 'At least 8 characters',
    active: 'Active',
    disabled: 'Access disabled',
    disable: 'Disable access',
    enable: 'Enable access',
    deleteConfirmTitle: 'Remove member?',
    deleteConfirmText: '“{name}” will lose access to the workspace. This cannot be undone.',
    empty: "It's just you so far",
    emptyHint: 'Invite colleagues: administrators, or viewers who can only see data.',
    created: 'Member added',
    updated: 'Changes saved',
    deleted: 'Member removed',
  },
  audit: {
    title: 'Activity log',
    subtitle: 'Who changed what and when',
    who: 'Who',
    what: 'Action',
    where: 'Company',
    when: 'When',
    result: 'Result',
    fields: 'Fields',
    ok: 'Success',
    denied: 'Denied',
    failed: 'Error',
    empty: 'No activity yet',
    emptyHint: 'Every change made by administrators will appear here.',
    platform: 'Platform',
    actions: {
      companyCreated: 'Company created',
      companyUpdated: 'Company details changed',
      companyDeleted: 'Company deleted',
      brandingUpdated: 'App branding changed',
      memberAdded: 'Team member added',
      memberUpdated: 'Team member changed',
      memberRemoved: 'Team member removed',
      paymentAdded: 'Payment added',
      paymentDeleted: 'Payment deleted',
      documentUploaded: 'Document uploaded',
      documentDeleted: 'Document deleted',
      posChecked: 'POS connection check',
      employeeProfile: 'Employee profile changed',
      employeePassword: 'Employee password changed',
      employeeDeleted: 'Employee account deleted',
      employeeSynced: 'Employee refreshed from POS',
      massSync: 'Bulk POS sync',
      pushSent: 'Campaign sent',
      pushDeleted: 'Campaign deleted',
      pushRuleCreated: 'Auto-rule created',
      pushRuleUpdated: 'Auto-rule changed',
      pushRuleDeleted: 'Auto-rule deleted',
      restaurantSaved: 'Venue saved',
      restaurantDeleted: 'Venue settings reset',
      ipBanned: 'IP banned',
      ipUnbanned: 'IP unbanned',
      ipWhitelisted: 'IP whitelisted',
      ipUnwhitelisted: 'IP removed from whitelist',
      sqlExecuted: 'SQL query executed',
      backupCreated: 'Backup created',
      cronChanged: 'Sync schedule changed',
      themeChanged: 'Seasonal theme changed',
      fileUploaded: 'File uploaded',
    },
    methods: {
      POST: 'Create',
      PUT: 'Update',
      PATCH: 'Change',
      DELETE: 'Delete',
    },
  },
  status: {
    company: { active: 'Active', trial: 'Trial', past_due: 'Past due', suspended: 'Suspended' },
    pos: { ok: 'POS online', error: 'POS error', degraded: 'History unavailable', unconfigured: 'Not configured', unknown: 'Not checked' },
    billing: { ok: 'Paid', due_soon: 'Due soon', overdue: 'Overdue', none: 'No plan' },
  },
  plans: { trial: 'Trial', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' },
  cycles: { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' },
  perMonth: '/mo',
  overview: {
    title: 'Platform overview',
    subtitle: 'Companies, employees, payments and POS health in one place',
    mrr: 'Monthly recurring revenue',
    arr: 'per year: {value}',
    companies: 'Companies',
    companiesHint: '{active} active',
    employees: 'Employees',
    employeesHint: '{registered} registered · +{new30} in 30 days',
    spend: 'Spend at venues (30 days)',
    spendHint: 'vs previous 30 days',
    chartTitle: 'Spend and top-ups',
    chartSubtitle: 'All companies, last 30 days',
    seriesSpend: 'Spend at venues',
    seriesTopup: 'Topped up by companies',
    companiesTitle: 'Company health',
    companiesSubtitle: 'POS, billing and activity of each company',
    activityTitle: 'Recent admin activity',
    allActivity: 'Full log',
    allCompanies: 'All companies',
    alertOverdue: 'Overdue payments: {count}',
    alertOverdueText: 'Companies past their next payment date.',
    alertPos: 'POS issues: {count}',
    alertPosText: 'POS is not configured or the last sync failed.',
    alertDueSoon: 'Payments due within 7 days: {count}',
    allGood: 'All good: payments on time, POS online',
    open: 'View',
  },
  companies: {
    title: 'Companies',
    subtitle: 'Platform clients: status, plan, POS and activity',
    new: 'New company',
    searchPlaceholder: 'Company name or code',
    filterStatus: 'Status',
    onlyProblems: 'Issues only',
    company: 'Company',
    plan: 'Plan',
    employees: 'Employees',
    balance: 'Card balance',
    spend30: 'Spend, 30d',
    pos: 'POS',
    billing: 'Billing',
    empty: 'No companies found',
    emptyHint: 'Change the filters or create your first company.',
    createTitle: 'New company',
    createStepCompany: 'Company',
    createStepAdmin: 'Company administrator',
    createStepPlan: 'Plan',
    name: 'Name',
    slug: 'System identifier (slug)',
    slugHint: 'Latin letters, digits, dash and underscore',
    companyCode: 'Company code',
    companyCodeHint: 'Employees enter it in the app. Empty — taken from slug',
    companyCodeEditHint: 'Employees enter it in the app',
    adminHint: 'Sign-in for the client’s company workspace. Can be added later under Team.',
    price: 'Price per period',
    cycle: 'Billing period',
    currency: 'Currency',
    created: 'Company created',
  },
  company: {
    back: 'All companies',
    openWorkspace: 'Open workspace',
    checkPos: 'Check POS',
    suspend: 'Suspend',
    activate: 'Activate',
    deleteCompany: 'Delete company',
    deleteTitle: 'Delete company?',
    deleteText: '“{name}” will be deleted with its documents and payments. A company with employees cannot be deleted — only suspended.',
    suspendTitle: 'Suspend company?',
    suspendText: 'Employees of “{name}” immediately lose access to the app, and admins to the workspace.',
    statusChanged: 'Status changed',
    deleted: 'Company deleted',
    tabs: {
      overview: 'Overview',
      profile: 'Profile',
      pos: 'Servio POS',
      branding: 'App',
      team: 'Team',
      billing: 'Plan & payments',
      documents: 'Documents',
      activity: 'Activity',
      telegram: 'Telegram bot',
    },
    kpi: {
      employees: 'Employees',
      employeesHint: '{registered} registered · {active7} active in 7 days',
      balance: 'Card balance',
      balanceHint: '{cards} active cards',
      spend: 'Spend, 30 days',
      topup: 'Topped up, 30 days',
    },
    last30: 'Last 30 days',
    topVenues: 'Top venues (30 days)',
    visits: '{count} visits',
    noVenues: 'No transactions in the last 30 days',
    posCard: 'POS',
    billingCard: 'Billing',
    nextPayment: 'Next payment',
    lastSync: 'Last sync',
    lastCheck: 'Last check',
    syncedCount: 'updated {synced}, failed {failed}',
    txSync: 'Transaction history',
    txSyncCount: '{cards} cards, {inserted} new transactions, {failed} failed',
    posDown: 'The main Servio service has been unavailable since {since}: employees cannot sign in and balances are not updating.',
    historyDown: 'POS responds, but the Servio system service (transaction history) has been unavailable since {since}. New transactions are not being recorded.',
  },
  profile: {
    general: 'General',
    legal: 'Legal details',
    contacts: 'Contacts',
    name: 'Public name',
    legalName: 'Legal name',
    edrpou: 'Tax ID',
    contactName: 'Contact person',
    contactPhone: 'Phone',
    contactEmail: 'Email',
    city: 'City',
    address: 'Address',
    locations: 'Number of venues',
    notes: 'Internal notes',
    notesHint: 'Visible only to you',
    regional: 'Regional settings',
    regionalHint: 'Affect reports, analytics and the time of automatic campaigns',
    timezone: 'Time zone',
    timezoneHint: 'POS transaction times and 10:00 automatic campaigns use this time zone',
    walletCurrency: 'Card currency',
    walletCurrencyHint: 'How balances and amounts are shown in the company workspace',
  },
  pos: {
    title: 'Servio connection',
    subtitle: 'Each company works with its own POS. Secret fields are not shown after saving.',
    posSection: 'POS API',
    systemSection: 'Transaction history (System API)',
    demoTitle: 'Demo POS',
    demoOnHint: 'The company runs on sample data without a real POS: employees 380000000001–380000000003, cards, history, QR. For App Store review and client demos.',
    demoOffHint: 'Turn on to run the company on sample data without a POS (App Store review, client demos).',
    servio_url: 'POS API URL',
    servio_term_id: 'Terminal TermID',
    servio_code: 'Terminal code',
    pos_code: 'POSCode',
    servio_url_sys: 'System API URL',
    system_term: 'System TermID',
    system_card: 'System CardCode',
    secretSaved: 'Saved — leave empty to keep',
    secretEmpty: 'Not set',
    defaultHint: 'For the default company empty fields fall back to the server .env.',
    check: 'Test connection',
    checking: 'Checking…',
    checkPos: 'POS API',
    checkSystem: 'System API',
    checkOk: 'Connected · {ms} ms',
    checkSkipped: 'Not configured',
    saved: 'POS settings saved',
  },
  branding: {
    title: 'App branding',
    subtitle: 'How the app looks to this company’s employees',
    colors: 'Colors',
    primary: 'Primary',
    secondary: 'Secondary',
    accent: 'Accent',
    background: 'Background',
    cardGradient: 'Card gradient',
    cornerRadius: 'Corner radius',
    logoUrl: 'Logo (URL)',
    headerTitle: 'Name in the app',
    tabsTitle: 'Tabs',
    tabsHint: 'Names and visibility of bottom bar tabs',
    preview: 'Preview',
    previewBalance: 'Balance',
    previewCard: 'Personal card',
    saved: 'Branding saved',
    tabNames: { wallets: 'My cards', notifications: 'Notifications', settings: 'Settings' },
  },
  billing: {
    title: 'Billing',
    subtitle: 'Plans, payments and overdue companies',
    mrr: 'MRR',
    arr: 'ARR',
    overdue: 'Overdue',
    dueSoon: 'Due in 7 days',
    plansTitle: 'Company plans',
    paymentsTitle: 'Payment history',
    monthly: 'Per month',
    nextDate: 'Next payment',
    contract: 'Contract',
    contractNumber: 'Contract number',
    contractDate: 'Contract date',
    planSection: 'Plan',
    addPayment: 'Add payment',
    amount: 'Amount',
    paymentDate: 'Payment date',
    periodStart: 'Period from',
    periodEnd: 'Period to',
    periodEndHint: 'Becomes the next payment date',
    method: 'Method',
    invoice: 'Invoice #',
    notes: 'Note',
    paymentStatus: 'Status',
    methods: { bank_transfer: 'Bank transfer', card: 'Card', cash: 'Cash', invoice: 'Invoice' },
    paymentStatuses: { completed: 'Paid', pending: 'Pending', refunded: 'Refunded' },
    deletePaymentTitle: 'Delete payment?',
    deletePaymentText: 'The payment record will be removed from history.',
    paymentAdded: 'Payment added',
    paymentDeleted: 'Payment deleted',
    noPayments: 'No payments yet',
    period: 'Period',
  },
  documents: {
    title: 'Documents',
    subtitle: 'Contracts, acts and invoices',
    upload: 'Upload',
    uploadTitle: 'New document',
    docTitle: 'Title',
    size: 'Size',
    type: 'Type',
    file: 'File',
    types: { contract: 'Contract', act: 'Act', invoice: 'Invoice', other: 'Other' },
    uploaded: 'Document uploaded',
    deleted: 'Document deleted',
    deleteTitle: 'Delete document?',
    deleteText: '“{name}” will be permanently deleted.',
    empty: 'No documents yet',
    emptyHint: 'Upload a contract or an act to keep everything at hand.',
    download: 'Open',
  },
  employees: {
    app: 'App',
    lastActive: 'Last activity',
    appFilter: { all: 'Any version', outdated: 'Haven’t updated the app', current: 'Up-to-date version', unknown: 'No app' },
    outdatedChip: 'Not updated to {version}: {count}',
    outdated: 'Outdated',
    upToDateBadge: 'Up to date',
    legacyAppShort: 'Old EnjoyFB',
    title: 'Employees',
    subtitleOwner: 'All employees of all companies on the platform',
    subtitleCompany: 'Company employees using the app',
    searchPlaceholder: 'Name or phone',
    company: 'Company',
    employee: 'Employee',
    phone: 'Phone',
    registered: 'Registration',
    registeredYes: 'Registered',
    registeredNo: 'Not completed',
    source: 'Source',
    sources: { all: 'All sources', app: 'App', telegram: 'Telegram' },
    joined: 'Joined',
    empty: 'No employees found',
    emptyHint: 'An employee appears here after their first sign-in to the app.',
    profileTab: 'Profile',
    cardsTab: 'Cards',
    historyTab: 'History',
    notificationsTab: 'Notifications',
    device: 'Device',
    appVersion: 'App version',
    legacyApp: 'old EnjoyFB app',
    updateAvailable: 'update {version} available',
    upToDate: 'up to date',
    lastLoginFrom: 'Last sign-in',
    registeredFrom: 'Registration',
    firstName: 'First name',
    lastName: 'Last name',
    middleName: 'Middle name',
    birthDate: 'Date of birth',
    sex: 'Sex',
    sexes: { 0: 'Not set', 1: 'Male', 2: 'Female' },
    sexAll: 'Any sex',
    syncPos: 'Refresh from POS',
    synced: 'Data refreshed from POS',
    resetPassword: 'New password',
    resetPasswordTitle: 'New password for employee',
    resetPasswordHint: 'Tell the employee the new password in person.',
    passwordChanged: 'Password changed',
    deleteEmployee: 'Delete account',
    deleteTitle: 'Delete employee account?',
    deleteText: 'The account, cards and history in the app will be deleted. The employee remains in Servio.',
    deleted: 'Account deleted',
    profileSaved: 'Profile saved',
    noCards: 'No cards',
    noHistory: 'No transactions',
    noNotifications: 'No notifications',
    balance: 'Balance',
    validUntil: 'Valid until',
    inactiveCard: 'Inactive',
    topup: 'Top-up',
    payment: 'Payment',
    read: 'Read',
    unread: 'Unread',
  },
  logs: {
    title: 'Event log',
    subtitle: 'What happens on the server: user actions, errors, sync',
    tabs: { events: 'Events', errors: 'Errors', performance: 'Performance' },
    total: 'Total events',
    errors: 'Errors',
    avgTime: 'Average time',
    period: 'Period',
    periods: { '1': '24 hours', '7': '7 days', '30': '30 days' },
    level: 'Level',
    levels: { info: 'Info', warn: 'Warning', error: 'Error', fatal: 'Fatal' },
    category: 'Category',
    categories: { user: 'Users', admin: 'Admins', system: 'System', cron: 'Sync', client: 'App', security: 'Security', ui: 'UI' },
    searchPlaceholder: 'Search event text',
    time: 'Time',
    event: 'Event',
    user: 'User',
    details: 'Event details',
    meta: 'Data',
    topErrors: 'Most frequent errors',
    topUsers: 'Users with most errors',
    occurrences: '{count} times',
    lastError: 'Last error',
    slowest: 'Slowest operations',
    calls: 'Calls',
    avg: 'Average',
    max: 'Max',
    empty: 'No events found',
    noErrors: 'No errors in this period',
    trendTitle: 'Events',
    seriesEvents: 'Events',
  },
  security: {
    title: 'Security',
    subtitle: 'Attacks on the server, banned and trusted IP addresses',
    tabs: { attacks: 'Attacks', banned: 'Banned IPs', whitelist: 'Whitelist' },
    totalAttacks: 'Total attacks',
    today: 'Today',
    banned: 'Banned IPs',
    topTypes: 'Attack types',
    ip: 'IP address',
    type: 'Type',
    request: 'Request',
    when: 'When',
    reason: 'Reason',
    attempts: 'Attempts',
    filterIp: 'Filter by IP',
    ban: 'Ban IP',
    banTitle: 'Ban IP address',
    unban: 'Unban',
    whitelistAdd: 'Add to whitelist',
    whitelistTitle: 'Trusted IP address',
    whitelistHint: 'Requests from this address bypass the WAF and are never banned.',
    remove: 'Remove',
    isBanned: 'Banned',
    banned_ok: 'IP banned',
    unbanned_ok: 'IP unbanned',
    whitelisted_ok: 'IP whitelisted',
    removed_ok: 'IP removed from whitelist',
    unbanConfirm: 'Unban {ip}?',
    unbanText: 'Requests from this address will be accepted again.',
    removeConfirm: 'Remove {ip} from whitelist?',
    removeText: 'Requests from this address will be inspected by the WAF again.',
    emptyAttacks: 'No attacks recorded',
    emptyBanned: 'No banned addresses',
    emptyWhitelist: 'Whitelist is empty',
    manualBan: 'Manual ban',
  },
  sql: {
    title: 'SQL console',
    subtitle: 'Direct database access. Be careful: changes cannot be undone.',
    tables: 'Tables',
    columns: 'Columns',
    query: 'Query',
    run: 'Run',
    running: 'Running…',
    rows: '{count} rows',
    changes: 'Rows changed: {count}',
    browse: 'Browse',
    hint: 'Ctrl + Enter to run',
    writeTitle: 'Run a modifying query?',
    writeText: 'This query changes data (not a SELECT). Make sure you have a backup.',
    writeConfirm: 'Run',
    emptyResult: 'Query executed, no rows',
    pickTable: 'Pick a table or write a query',
    limited: 'Showing the first {count} rows',
  },
  system: {
    title: 'System settings',
    subtitle: 'POS sync, backups, certificate and seasonal theme',
    sync: 'POS synchronization',
    syncHint: 'How often the server refreshes all employee cards from company POS systems.',
    interval: 'Interval, minutes',
    syncNow: 'Sync now',
    syncStarted: 'Sync started',
    syncProgress: '{percent}% done',
    lastSync: 'Last sync',
    nextSync: 'Next',
    backup: 'Backup',
    backupHint: 'Database, files and settings archive is uploaded to a private GitHub repository and sent to Telegram. Automatically every day at 01:00.',
    backupNow: 'Create backup now',
    backupDone: 'Backup created',
    ssl: 'SSL certificate',
    sslDomain: 'Domain',
    sslValidTo: 'Valid until',
    sslDays: 'Days left',
    sslIssuer: 'Issuer',
    sslRenew: 'Renew certificate',
    sslUnavailable: 'Could not read the certificate',
    sslOutput: 'Renewal log',
    appVersion: 'App versions',
    storeRefresh: 'Check App Store',
    storeVersion: 'On the App Store now: version {version}',
    storeReleased: 'released {date}',
    storeChecked: 'checked {at}',
    storeUnknown: 'App Store version not fetched yet',
    autoLatest: 'Latest version — automatically from the App Store',
    autoLatestHint: 'When a new version is out, the app suggests updating (once per version)',
    latestAutoHint: 'Taken from the App Store',
    storeUrlAuto: 'If empty — {url}',
    appVersionHint: 'Older iOS app versions are asked to update. Leave empty for no limit.',
    minVersion: 'Minimum version',
    minVersionHint: 'Below it the update is required',
    latestVersion: 'Latest version',
    latestVersionHint: 'Below it the update is suggested',
    appStoreUrl: 'App Store link',
    seasonal: 'Seasonal app theme',
    seasonalHint: 'Holiday look for all companies (snow, emoji).',
    themes: { standard: 'Standard', new_year: 'New Year' },
    themeSaved: 'Theme changed',
    saved: 'Settings saved',
  },
  servio: {
    title: 'Servio connection',
    subtitle: 'Checked every hour and on every POS request',
    problems: 'Connection problems: {count}',
    pos: 'Main service',
    posHint: 'Employee sign-in, balances, QR codes',
    system: 'System service',
    systemHint: 'Transaction history',
    states: { ok: 'Online', down: 'Unavailable', unknown: 'Not checked' },
    checked: 'checked {time}',
    since: 'down since {time}',
    lastCheck: 'Last check',
    checkNow: 'Check',
    noCompanies: 'No companies with a connected POS',
  },
  twofa: {
    title: 'Two-factor sign-in',
    subtitle: 'A code from your phone in addition to the password',
    status: 'Status',
    on: 'On',
    off: 'Off',
    intro: 'Even if someone learns your password, they cannot sign in without your phone. You need Google Authenticator, 1Password or a similar app.',
    start: 'Turn on',
    step1: 'Scan the QR code in your authenticator app (or enter the key manually).',
    step2: 'Enter the 6-digit code the app shows.',
    qrAlt: 'QR code for the authenticator app',
    confirm: 'Confirm and turn on',
    enabled: 'Two-factor sign-in is on',
    disable: 'Turn off',
    disableHint: 'To turn it off, enter the current code from your authenticator app.',
    disabled: 'Two-factor sign-in is off',
    reset: 'Reset 2FA',
    resetHint: 'If a colleague lost their phone. All their sessions will end.',
  },
  invites: {
    tab: 'Invitations',
    invite: 'Invite',
    inviteTitle: 'Invite an employee',
    inviteHint: 'A personal link for one phone number to register in the app',
    phoneHint: 'The number registered in the POS',
    name: 'Name (optional)',
    namePlaceholder: 'To greet them on the invitation page',
    validity: 'Valid for',
    days: { 1: '1 day', 3: '3 days', 7: '7 days', 14: '14 days', 30: '30 days' },
    create: 'Create link',
    readyTitle: 'Invitation ready',
    another: 'Another one',
    link: 'Link',
    linkHint: 'Send it via Telegram, Viber or SMS. The app opens with the company and phone already filled in.',
    qrAlt: 'Invitation QR code',
    qrHint: 'Or show the QR code — the employee scans it with the phone camera',
    downloadQr: 'Download QR (PNG)',
    title: 'Invitation',
    created: 'Created',
    validUntil: 'Valid until',
    copyLink: 'Copy link',
    showQr: 'Show QR code',
    revoke: 'Revoke',
    revokeTitle: 'Revoke this invitation?',
    revokeText: 'The link for {phone} will stop working.',
    revoked: 'Invitation revoked',
    statuses: { pending: 'Pending', used: 'Used', expired: 'Expired', revoked: 'Revoked' },
    empty: 'No invitations yet',
    emptyHint: 'Send an employee a link — they will register in a minute.',
    modeTitle: 'Registration by invitation only',
    modeInviteHint: 'A new employee can register only with a personal link for their own number. No one else can claim their account.',
    modeOpenHint: 'Right now anyone who knows the company code and an employee’s number can register. We recommend turning this on.',
  },
  home: {
    paymentSoon: 'Subscription payment due in {days} d',
    paymentToday: 'Subscription payment is due today',
    paymentOverdue: 'Subscription payment is {days} d overdue',
    paymentDetails: 'Due {date}, amount {amount}. If you’ve already paid — thank you!',
    greeting: 'Welcome, {name}',
    subtitle: 'How your employees use the app, at a glance',
    companyCode: 'Company code',
    companyCodeHint: 'Employees enter this code in the app the first time they sign in.',
    copy: 'Copy',
    copied: 'Copied',
    howToJoin: 'How an employee gets started',
    steps: {
      1: 'Install the app and enter the company code',
      2: 'Sign in with the phone number registered in the POS',
      3: 'Create a password — the card appears automatically',
    },
    quick: 'Quick actions',
    quickPush: 'Send a campaign',
    quickBranding: 'Customize the app',
    quickTeam: 'Invite a colleague',
    quickVenues: 'Set up venues',
    recent: 'Recent team activity',
    noRecent: 'No activity yet',
    posOk: 'POS data is syncing',
    posProblem: 'There is a POS problem — we are aware and working on it',
    posHistoryDown: 'Balances are updating, but new transactions are temporarily not reaching the history — we are working on it',
    posNotConfigured: 'POS is not connected yet. Please contact support.',
  },
  analytics: {
    title: 'Analytics',
    subtitleCompany: 'Employee spending, company top-ups and popular venues',
    subtitleOwner: 'Spending and top-ups across all companies or a selected one',
    period: 'Period',
    periods: { 7: '7 days', 30: '30 days', 90: '90 days', 365: 'Year' },
    allCompanies: 'All companies',
    export: 'Export CSV',
    exported: 'File downloaded',
    spend: 'Spent at venues',
    checks: 'Checks',
    avgCheck: 'Average check',
    topup: 'Topped up by company',
    spenders: 'Used their card',
    spendersHint: 'of {cards} cards',
    vsPrev: 'vs previous period',
    engagement: 'Engagement',
    inApp: 'In the app',
    inAppHint: 'registered of {total}',
    active7: 'Active in 7 days',
    idle: 'No spending in period',
    idleHint: 'cards were not used',
    negative: 'Negative balance',
    negativeHint: 'total {sum}',
    trend: 'Spending and top-ups by day',
    venues: 'Venues',
    venue: 'Venue',
    visits: 'Visits',
    sum: 'Amount',
    share: 'Share',
    people: 'Employees',
    lastVisit: 'Last visit',
    topEmployees: 'Most active employees',
    employee: 'Employee',
    heatmap: 'When employees visit venues',
    heatmapHint: 'Number of checks by weekday and hour',
    fewer: 'Fewer',
    more: 'More',
    weekdays: { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' },
    heatCell: '{day}, {hour}:00 — {count} checks',
    byCompany: 'By company',
    noData: 'No transactions in this period',
    unknown: 'No name',
  },
  support: {
    title: 'Support',
    subtitle: 'Requests from employees of all companies — from the app and the Telegram bot',
    botSettings: 'Support bot',
    tabs: { open: 'Open', new: 'New', in_progress: 'In progress', resolved: 'Resolved', all: 'All' },
    status: { new: 'New', in_progress: 'In progress', resolved: 'Resolved' },
    searchPlaceholder: 'Name, phone or #number',
    company: 'Company',
    allCompanies: 'All companies',
    empty: 'No requests',
    emptyHint: 'Employee requests will appear here.',
    pick: 'Select a request',
    pickHint: 'Conversation, device and app errors — on the right.',
    you: 'You',
    takeInWork: 'Take on',
    resolve: 'Resolve',
    noDevice: 'Unknown device',
    created: 'Created',
    appErrors: 'App errors: {count}',
    replyPlaceholder: 'Reply to the employee — delivered to Telegram and as a push to the app',
    replyHint: 'Ctrl + Enter — send',
    send: 'Send',
    sent: 'Reply sent',
    notDelivered: 'Saved, but the employee is not reachable in Telegram right now',
    setup: {
      title: 'Connect the support bot',
      text: 'Employees will write to a Telegram bot (button in the app), and requests will appear here and in your Telegram.',
      step1: 'Open @BotFather in Telegram and send /newbot.',
      step2: 'Enter a name (e.g. “Spoilty Support”) and a username ending with bot (e.g. SpoiltySupportBot).',
      step3: 'BotFather sends a token like 1234567890:AA… — copy it.',
      step4: 'Paste the token below and press “Connect”. Then connect your Telegram to receive requests.',
      token: 'Bot token',
      connect: 'Connect',
      connected: 'Bot @{name} connected',
    },
    owner: {
      title: 'Connect your Telegram',
      text: 'Press the button — the bot opens, press “Start”. Requests will then come to your Telegram; reply to a request card to answer.',
      connect: 'Connect my Telegram',
      check: 'Check',
    },
    settings: {
      bot: 'Bot',
      owner: 'Requests go to',
      ownerMissing: 'Owner Telegram not connected',
      replace: 'Replace the bot — paste another bot’s token:',
      disconnect: 'Disconnect bot',
      disconnectConfirm: 'Disconnect the support bot? Employees won’t be able to write until you connect it again.',
    },
  },
  tgBot: {
    title: 'Telegram bot for employees',
    intro: 'For employees with Android or those who don’t want to install the app: the company’s Telegram bot shows cards, balance, a QR code for payment, transaction history and top-up notifications. The bot belongs to your company and carries your name.',
    stepsTitle: 'How to set up the bot',
    steps: {
      s1: { title: 'Open @BotFather in Telegram', text: 'Telegram’s official bot for creating bots (with a blue check mark). Press “Start”.' },
      s2: { title: 'Send the /newbot command', text: 'BotFather asks for the bot name — employees see it, e.g. “Bravo Cards”.' },
      s3: { title: 'Pick a username', text: 'Latin letters, no spaces, must end with bot — e.g. BravoCardsBot. If it’s taken, BotFather asks for another.' },
      s4: { title: 'Copy the token', text: 'BotFather sends a token like 1234567890:AAH… Don’t forward it to anyone — it is the key to the bot.' },
      s5: { title: 'Optionally — a logo', text: '/setuserpic → choose the bot → send the company logo (square). The server sets up the description and commands itself.' },
      s6: { title: 'Paste the token here and press “Connect”', text: 'The server checks the token, connects the bot and shows a link and QR code to share with employees.' },
    },
    token: 'Bot token',
    connect: 'Connect',
    connected: 'Bot @{name} connected',
    connectedTitle: 'Bot connected',
    linked: 'Employees connected: {count}',
    howEmployees: 'An employee opens the bot, presses “Start” and shares their phone number. If the number belongs to your company’s employees, they see their cards. Top-up notifications go to the bot for those who don’t use the app.',
    inviteText: '{company} has a Telegram bot for cards: balance, QR code for payment and history.\nOpen {link}, press “Start” and share your phone number.',
    copyInvite: 'Copy invitation',
    copied: 'Invitation copied',
    open: 'Open bot',
    qrTitle: 'Bot QR code',
    qrHint: 'Print it or show it to employees — the phone camera opens the bot.',
    downloadQr: 'Download QR',
    replace: 'Replace the bot — paste another bot’s token:',
    disconnect: 'Disconnect bot',
    disconnectConfirm: 'The bot will stop answering employees until you connect it again. The bot itself won’t be deleted from Telegram.',
    viewOnly: 'A company administrator can connect the bot.',
  },
  push: {
    cardEvents: {
      title: 'Card notifications',
      hint: 'Sent automatically when the POS updates the card balance (checked hourly). Disabled ones go neither to push nor to the in-app feed.',
      types: { top_up: 'Card top-up', new_card: 'New card', negative_balance: 'Balance went negative' },
      minAmount: 'Minimum amount, ₴',
      minAmountHint: 'Smaller top-ups (e.g. refunds) are not notified. 0 — all of them.',
      placeholders: 'Placeholders: {name} — employee name, {card} — card number, {amount} — top-up amount, {balance} — balance after the change.',
      quiet: 'Quiet hours',
      quietFrom: 'From',
      quietTo: 'To',
      quietHint: 'No push during this time: the entry appears in the in-app feed right away, and the push arrives when quiet hours end.',
      sample: { name: 'Olena' },
    },
    title: 'Campaigns',
    subtitle: 'In-app messages and push notifications for employees',
    tabs: { compose: 'New campaign', history: 'History', rules: 'Automatic' },
    company: 'Company',
    pickCompany: 'Choose a company',
    pickCompanyHint: 'A campaign goes to the employees of one company.',
    audience: 'Audience',
    audiences: { all: 'Everyone', male: 'Men', female: 'Women', phones: 'By phone' },
    phones: 'Phone numbers',
    phonesHint: 'One number per line or comma-separated, format 380…',
    recipients: '{count} recipients',
    titleField: 'Title',
    message: 'Message',
    image: 'Image',
    imageHint: 'Optional. JPG or PNG up to 10 MB.',
    removeImage: 'Remove',
    ttl: 'Valid for',
    ttlOptions: { '': 'No limit', 6: '6 hours', 24: '1 day', 72: '3 days', 168: '1 week' },
    ttlHint: 'If the phone is offline longer, the push is dropped but the message stays in the app.',
    preview: 'Preview',
    previewTitle: 'Message title',
    previewMessage: 'Message text',
    now: 'now',
    send: 'Send',
    confirmTitle: 'Send this campaign?',
    confirmText: '{count} employees will receive it. It cannot be recalled once sent.',
    sent: 'In-app feed: {count}, push delivered: {pushed}',
    noRecipients: 'No employees match this campaign',
    sentAt: 'Sent',
    delivered: 'Received',
    readRate: 'Read',
    deleteTitle: 'Delete this campaign?',
    deleteText: 'The message will disappear from employees’ notification feed in the app.',
    deleted: 'Campaign deleted',
    emptyHistory: 'No campaigns yet',
    rulesIntro: 'Automatic campaigns go out every day at 10:00 (Kyiv) to the employees they apply to.',
    newRule: 'New rule',
    editRule: 'Campaign rule',
    ruleType: 'Trigger',
    ruleTypes: { birthday: 'Birthday', negative_balance: 'Negative balance' },
    ruleTypeHints: {
      birthday: 'On the employee’s birthday.',
      negative_balance: 'A reminder while the balance stays negative; at most once a week. The first negative-balance alert is sent by the system.',
    },
    placeholders: 'You can use {name} for the first name and {balance} for the card balance.',
    active: 'On',
    paused: 'Off',
    lastRun: 'Last sent',
    todayWillGet: 'Would receive today: {count}',
    checkToday: 'Who gets it today',
    ruleSaved: 'Rule saved',
    ruleDeleted: 'Rule deleted',
    deleteRuleTitle: 'Delete this rule?',
    deleteRuleText: 'The automatic campaign “{name}” will no longer be sent.',
    emptyRules: 'No automatic campaigns yet',
    emptyRulesHint: 'For example, birthday greetings.',
    uploadFailed: 'Could not upload the image',
    systemTitle: 'Automatic notifications, last 30 days',
    systemHint: 'Sent on their own when the POS reports a change to an employee’s card.',
    systemTypes: { new_card: 'New card', top_up: 'Top-up', negative_balance: 'Balance went negative' },
    systemRead: '{pct}% read',
  },
  venues: {
    title: 'Venues',
    subtitle: 'How venues look in the app: name, logo and opening hours',
    search: 'Search venues',
    fromPos: 'Name in POS',
    displayName: 'Name in the app',
    schedule: 'Opening hours',
    schedulePlaceholder: 'Mon–Fri 09:00–22:00, Sat–Sun 10:00–23:00',
    scheduleHint: 'The app uses the hours to show “Open”, “Closing soon”, “Closed”.',
    scheduleUnderstood: 'In the app',
    scheduleNotUnderstood: 'Could not read the hours — the app will show the text only, without an Open/Closed status. Example: Mon–Fri 09:00–22:00, Sat–Sun 10:00–23:00',
    allDay: '24 hours',
    top: {
      title: 'Most popular venue',
      hint: 'In the app it is shown first in the list, with a golden border and visit stats. It is based on employee visits over the selected period, or pick a venue manually.',
      now: 'Now in the app',
      none: 'None yet: no visits in this period.',
      visits: '{count} visits',
      days: 'in {count} days',
      allTime: 'all time',
      period: 'Period',
      leader: 'Leader',
      auto: 'Automatic',
      autoWith: 'Automatic — {name}',
      manualBadge: 'Picked manually',
    },
    dayOff: 'closed',
    day1: 'Mon', day2: 'Tue', day3: 'Wed', day4: 'Thu', day5: 'Fri', day6: 'Sat', day7: 'Sun',
    servioId: 'Servio ID',
    logo: 'Logo',
    logoHint: 'PNG, JPG, SVG or WebP up to 1 MB. Square with a background works best. SVG is converted to PNG automatically.',
    logoLightWarning: 'The logo is light on a transparent background — it is barely visible on the white tile in the app. Better upload a version with a colored background.',
    logoTooBig: 'File is too large — 1 MB max',
    uploadLogo: 'Upload logo',
    removeLogo: 'Remove logo',
    notConfigured: 'Not set up',
    configured: 'Set up',
    saved: 'Venue saved',
    reset: 'Reset',
    resetTitle: 'Reset this venue?',
    resetText: 'Logo, name and hours of “{name}” will be removed. The venue stays in the list with its POS name.',
    resetDone: 'Venue reset',
    empty: 'No venues yet',
    emptyHint: 'A venue appears here after an employee’s first transaction there.',
    filters: { all: 'All', configured: 'Configured', new: 'Not configured', service: 'Service' },
    inApp: 'In the app',
    txCount: 'Transactions: {count}',
    lastTx: 'last {date}',
    alsoInPos: 'Also in POS as',
    merge: 'Merge',
    mergeTitle: 'Merge into “{name}”',
    mergeHint: 'Pick POS names that mean this same venue (old names, other spelling). Transactions, visits and analytics are counted together, and the history shows one logo and name. POS data is not changed.',
    mergeConfirm: 'Merge ({count})',
    merged: 'Merged: {count}',
    unmerge: 'Unmerge {name}',
    unmerged: 'Name unmerged',
    markService: 'Service: not a venue (e.g. the central office where cards are topped up)',
    markedService: 'Marked as service',
    unmarkService: 'Restore',
    unmarkedService: 'Restored to venues',
    serviceHint: 'POS names that are not venues — e.g. the central office where cards are topped up. Hidden from the app and venue analytics.',
    serviceEmpty: 'No service names',
    tabTitle: '“Venues” tab in the app',
    tabOnHint: 'Employees see the list of venues with addresses, map, opening hours and phone. Hidden venues are not shown.',
    tabOffHint: 'Turn on so employees can see in the app where the card works: addresses, map, opening hours.',
    noAddress: 'No address',
    hidden: 'Hidden',
    category: 'Venue type',
    categoryPlaceholder: 'Café, restaurant, coffee shop…',
    address: 'Address',
    addressPlaceholder: 'Kyiv, 1 Khreshchatyk St',
    coords: 'Map coordinates',
    coordsHint: 'Copy from Google Maps: right-click the place → first line, e.g. 50.4501, 30.5234',
    coordsInvalid: 'Coordinates as “50.4501, 30.5234”',
    openMap: 'Check on map',
    phone: 'Phone',
    description: 'Description',
    descriptionPlaceholder: 'What’s on the menu, how to find it, takeaway…',
    menuUrl: 'Menu link',
    menuUrlHint: 'Website, PDF or a menu page — the app shows a “Menu” button',
    openMenu: 'Open',
    showInApp: 'Show in the app',
    showInAppHint: 'Turn off for venues that shouldn’t be listed (e.g. a staff kitchen)',
    sortOrder: 'Order in the list',
    sortOrderHint: 'Lower number — higher up',
  },
  reminders: {
    title: 'Payment reminders',
    subtitle: 'To you via Telegram, to clients by email — when a subscription payment is coming up or overdue',
    channelTelegram: 'Owner Telegram bot',
    channelTelegramHint: 'Not set up: TELEGRAM_BOT_TOKEN and TELEGRAM_CHAT_ID in the server .env',
    channelEmail: 'Email for client letters',
    channelEmailHint: 'Not set up: EMAIL_HOST, EMAIL_USER, EMAIL_PASS in the server .env',
    hour: 'Sending time',
    hourHint: 'Daily, Kyiv time',
    ownerTitle: 'To me via Telegram',
    ownerHint: 'One summary: who pays soon and who is overdue',
    daysBefore: 'Days before payment',
    daysBeforeHint: 'Comma-separated, 0 — on the due date. E.g. 7, 3, 1, 0',
    overdueDaily: 'Daily while overdue',
    overdueDailyHint: 'Remind every day until the payment is recorded',
    testTelegram: 'Send a test summary',
    customerTitle: 'To clients by email',
    customerHint: 'Email to the company (contact email or a separate billing email in the company card)',
    overdueDays: 'After overdue, on days',
    overdueDaysHint: 'E.g. 1, 3, 7 — on the 1st, 3rd and 7th day overdue',
    placeholders: 'You can use in the text:',
    beforeLetter: 'Before the due date',
    overdueLetter: 'After overdue',
    subject: 'Subject',
    body: 'Text',
    testCompany: 'Company for the sample',
    testTo: 'Send sample to',
    testBefore: 'Sample: before due',
    testOverdue: 'Sample: overdue',
    testSent: 'Test message sent',
    companiesTitle: 'Upcoming payments',
    noCompanies: 'No companies with a plan and a due date',
    noCompaniesHint: 'Set the price and next payment date in the company “Plan & payments” tab',
    when: 'When',
    today: 'today',
    inDays: 'in {days} d',
    overdueDaysLabel: '{days} d overdue',
    recipient: 'Billing email',
    noEmail: 'no email',
    emailsOn: 'Client emails',
    emailsOnHint: 'Turn off if the company shouldn’t get reminders (e.g. paid by contract)',
    billingEmail: 'Billing email',
    billingEmailHint: 'If empty — the contact email ({email})',
    logTitle: 'Sending log',
    logEmpty: 'No reminders sent yet',
    sentAt: 'When',
    channel: 'Where',
    sent: 'Sent',
    failed: 'Error',
    preview: 'What goes out today',
    previewTitle: 'Today’s reminders',
    previewOwner: 'To you via Telegram',
    previewCustomers: 'To clients by email',
    previewNone: 'Nothing',
    previewNote: 'This is sent automatically every day at the chosen time. The same email is never sent to a client twice.',
    sendNow: 'Send now',
    runDone: 'Done: Telegram — {owner} companies, emails — {sent}, errors — {failed}',
  },
  reliability: {
    title: 'Reliability',
    subtitle: 'Daily database copy with a restore check and site availability monitoring. Problems are reported to you via Telegram',
    checkNow: 'Make and check a copy',
    backupTitle: 'Database backup',
    backupNever: 'Not checked yet',
    backupOk: 'Copy created and checked',
    backupFailed: 'The copy failed the check',
    backupOkAt: 'Checked {at}: opens, data intact',
    backupFailedAt: 'Error {at}',
    backupCounts: 'Employees {users}, cards {cards}, transactions {tx}',
    kept: '{count} kept',
    backupSchedule: 'Daily at 03:30 on the server + a GitHub copy at 01:00',
    uptimeTitle: 'Site availability',
    watchdogOff: 'Watchdog is not running',
    siteUp: 'Up, response {ms} ms',
    siteDown: 'Down since {since}',
    lastIncident: 'Last outage: {at}, ~{minutes} min',
    uptimeHint: 'Checked every minute; down for 5 min — a Telegram message',
  },
  myCompany: {
    title: 'My company',
    subtitle: 'Company details, plan, payments and documents',
    tabs: { details: 'Details', billing: 'Plan & payments', documents: 'Documents', telegram: 'Telegram bot' },
    details: 'Company details',
    contacts: 'Contact person',
    changeHint: 'To change company details or your plan, contact support.',
    plan: 'Plan',
    status: 'Status',
    price: 'Price',
    nextPayment: 'Next payment',
    contract: 'Contract',
    payments: 'Payments',
  },
  placeholder: {
    title: 'Section in progress',
    text: 'This section is being redesigned.',
  },
};

export default en;
